import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatSelect } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';
import { HOURS, MINUTES } from 'src/app/core/constants/time.constants';
import { NativeBridgeService } from 'src/app/core/services/native-bridge.service';
import { DetailPaymentService } from 'src/app/pages/detail-pay/services/detail.payment.service';
import { runtimeEnv } from '../../../../../environments/env-loader';
import { BehaviorSubject } from 'rxjs';
import { CloverService } from 'src/app/core/services/clover.service';
import { dateRangeValidator } from './movement-filter-validate';

@Component({
  selector: 'app-movements-filter',
  templateUrl: './movements-filter.component.html',
  styleUrls: ['./movements-filter.component.scss']
})
export class MovementsFilterComponent implements OnInit, OnChanges {
  @Input() branchesValuesForSelect: any[];
  @Input() cashiersValuesForSelect: any[];
  @Input() methodsValuesForSelect: any[];
  @Input() statesValuesForSelect: any[];
  @Input() showBranchesAndCashiers: boolean;
  @Input() refreshDates: boolean;
  @Input() showOptions: boolean;
  @Input() isLoadingExcel: boolean;
  @Input() userResponsible: boolean;
  @Output() filterEmitter: EventEmitter<any> = new EventEmitter();
  @Output() cleanFilterEmitter: EventEmitter<any> = new EventEmitter();
  @Output() getStatesSelectValuesEmitter: EventEmitter<any> = new EventEmitter();
  @Output() getCashiersSelectValuesEmitter: EventEmitter<any> = new EventEmitter();
  @Output() refreshListEmitter: EventEmitter<any> = new EventEmitter();
  @Output() downloadExcelEmitter: EventEmitter<any> = new EventEmitter();
  @Output() dateRangeChangedEmitter: EventEmitter<any> = new EventEmitter();

  @ViewChild('estadoMatSelect') selectEstado: MatSelect;
  @ViewChild('medioPagoMatSelect') medioPagoSelect: MatSelect;
  @ViewChild('branchMatSelect') selectBranch: MatSelect;
  @ViewChild('cashierMatSelect') selectCashier: MatSelect;
  @ViewChild('picker') pickerTo: MatDatepicker<1>;
  @ViewChild('picker1') pickerFrom: MatDatepicker<2>;
  maxDate: Date = new Date();
  filterForm: FormGroup;
  searchBranch = new FormControl('');
  searchCashier = new FormControl('');
  hours = HOURS;
  minutes = MINUTES;
  minDate: Date;
  maxDateTo: Date;
  disableExport: boolean = true;
  showDefaultRangeBanner: boolean = true;
  showExceeds31DaysBanner: boolean = false;
  isDefaultDateRange: boolean = true;

  private messageReceivedSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public messageReceived$ = this.messageReceivedSubject.asObservable();
  appOpenedWithClover: boolean = false;

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
    private detailStorage: DetailPaymentService,
    private nativeBridgeService: NativeBridgeService,
    private cloverService: CloverService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshDates']?.previousValue != changes['refreshDates']?.currentValue) {
      this.filterForm?.get('dateFrom').patchValue(null);
      this.filterForm?.get('hourFrom').patchValue('00');
      this.filterForm?.get('minutesFrom').patchValue('00');
      this.filterForm?.get('dateTo').patchValue(null);
      this.filterForm?.get('hourTo').patchValue('23');
      this.filterForm?.get('minutesTo').patchValue('59');
    }
  }

  ngOnInit(): void {
    this.minDate = new Date(runtimeEnv.MINDATE);
    this.nativeBridgeService.$messageReceived.subscribe((messageReceived) => {
      this.messageReceivedSubject.next(messageReceived);
      if (this.cloverService.getTerminalId()) {
        this.appOpenedWithClover = true;
      }
      if (this.filterForm && this.detailStorage.hasFilterValues()) {
        this.setFiltros();
      }
    });

    this.createFilterForm();

    // Establecer fechas por defecto si no hay filtros guardados
    if (!this.detailStorage.hasFilterValues()) {
      this.setDefaultDateRange();
    } else {
      // Si hay filtros guardados, cargarlos en el formulario
      this.setFiltros();
    }

    this.filterForm.get('dateFrom')?.valueChanges.subscribe((dateFromValue) => {
      console.log('dateFromValue', dateFromValue);
      this.clearDateTo();
      this.setMaxDate(dateFromValue);
      this.checkDateRangeChanges();
    });

    this.filterForm.get('dateTo')?.valueChanges.subscribe(() => {
      this.checkDateRangeChanges();
    });

    this.searchBranch.valueChanges.pipe(debounceTime(150)).subscribe((value) => {
      value
        ? (this.branchesValuesForSelect = this.branchesValuesForSelect.filter((branch) =>
            branch.display.toUpperCase().includes(value.toUpperCase())
          ))
        : null;
    });

    this.searchCashier.valueChanges.pipe(debounceTime(150)).subscribe((value) => {
      value
        ? (this.cashiersValuesForSelect = this.cashiersValuesForSelect.filter((cashier) =>
            cashier.display.toUpperCase().includes(value.toUpperCase())
          ))
        : null;
    });

    this.filterForm.valueChanges.subscribe((values) => {
      const hasDateFrom = !!values.dateFrom;
      const hasDateTo = !!values.dateTo;
      const hasPaymentMethods = Array.isArray(values.paymentMethods)
        ? values.paymentMethods.length > 0
        : !!values.paymentMethods;
      const hasNoDateRangeErrors =
        !this.filterForm.errors?.dateRangeError && !this.filterForm.errors?.dateRangeExceeds31Days;
      this.disableExport = !(hasDateFrom && hasDateTo && hasPaymentMethods && hasNoDateRangeErrors);
    });
  }

  setMaxDate(dateFrom: Date): void {
    if (dateFrom) {
      const maxDateFrom = new Date(dateFrom);
      maxDateFrom.setDate(maxDateFrom.getDate() + 30);
      this.maxDateTo = maxDateFrom > new Date() ? new Date() : maxDateFrom;
    }
  }

  clearDateTo(): void {
    const dateToControl = this.filterForm.get('dateTo');
    dateToControl?.setValue(null);
    // Marcar como touched para mostrar el error visualmente
    dateToControl?.markAsTouched();
    // Forzar la actualización de validación
    dateToControl?.updateValueAndValidity();
  }

  isMobileView(): boolean {
    return window.innerWidth <= 1125; // Puedes ajustar el valor según tu diseño responsivo
  }

  translate(key: string): string {
    return this.translateService.instant(key);
  }

  createFilterForm() {
    this.filterForm = this.fb.group(
      {
        paymentMethods: null,
        paymentStates: null,
        dateFrom: [null, Validators.required],
        hourFrom: '00',
        minutesFrom: '00',
        dateTo: [null, Validators.required],
        hourTo: '23',
        minutesTo: '59',
        branches: null,
        cashiers: null
      },
      { validators: dateRangeValidator('dateFrom', 'dateTo') }
    );

    this.filterForm.get('cashiers').disable;
    this.filterForm.get('dateFrom').disable;
    this.filterForm.get('dateTo').disable;

    this.setValueChangesToForm();
  }

  setValueChangesToForm() {
    this.filterForm.get('paymentMethods').valueChanges.subscribe((newVal) => {
      this.medioPagoSelect?.close();
      newVal?.length ? this.getStatesSelectValuesEmitter.emit(newVal) : this.getStatesSelectValuesEmitter.emit([]);
    });

    this.filterForm.get('paymentStates').valueChanges.subscribe(() => {
      this.selectEstado?.close();
    });

    this.filterForm.get('branches').valueChanges.subscribe((newVal) => {
      if (newVal?.length) {
        this.filterForm.get('cashiers').enable;
        this.selectBranch?.close();
        this.getCashiersSelectValuesEmitter.emit(newVal);
      } else {
        this.filterForm.get('cashiers').disable;
      }
    });

    this.filterForm.get('cashiers').valueChanges.subscribe(() => {
      this.selectCashier?.close();
    });
  }

  setFiltros() {
    if (this.detailStorage.movementFilters.currentMethods?.length) {
      this.filterForm.get('paymentMethods').patchValue(this.detailStorage.movementFilters.currentMethods);
      this.getStatesSelectValuesEmitter.emit(this.filterForm.get('paymentMethods').value);
    }

    if (this.detailStorage.movementFilters.currentStates?.length) {
      this.filterForm.get('paymentStates').patchValue(this.detailStorage.movementFilters.currentStates);
    }

    if (this.detailStorage.movementFilters.dateFrom) {
      this.filterForm.get('dateFrom').patchValue(this.detailStorage.movementFilters.dateFrom);
    }

    if (this.detailStorage.movementFilters.hourFrom) {
      this.filterForm.get('hourFrom').patchValue(this.detailStorage.movementFilters.hourFrom);
    } else {
      this.filterForm.get('hourFrom').patchValue('00');
    }

    if (this.detailStorage.movementFilters.minutesFrom) {
      this.filterForm.get('minutesFrom').patchValue(this.detailStorage.movementFilters.minutesFrom);
    } else {
      this.filterForm.get('minutesFrom').patchValue('00');
    }

    if (this.detailStorage.movementFilters.dateTo) {
      this.filterForm.get('dateTo').patchValue(this.detailStorage.movementFilters.dateTo);
    }

    if (this.detailStorage.movementFilters.hourTo) {
      this.filterForm.get('hourTo').patchValue(this.detailStorage.movementFilters.hourTo);
    } else {
      this.filterForm.get('hourTo').patchValue('23');
    }

    if (this.detailStorage.movementFilters.minutesTo) {
      this.filterForm.get('minutesTo').patchValue(this.detailStorage.movementFilters.minutesTo);
    } else {
      this.filterForm.get('minutesTo').patchValue('59');
    }

    if (this.detailStorage.movementFilters.currentBranches?.length) {
      this.filterForm.get('branches').patchValue(this.detailStorage.movementFilters.currentBranches);
    }

    if (this.detailStorage.movementFilters.currentCashiers?.length) {
      this.filterForm.get('cashiers').patchValue(this.detailStorage.movementFilters.currentCashiers);
    }
  }

  stateButtonClean() {
    if (this.detailStorage.movementFilters?.dateTo) {
      return false;
    } else if (this.detailStorage.movementFilters?.dateFrom) {
      return false;
    } else if (this.detailStorage.movementFilters?.currentStates?.length) {
      return false;
    } else if (this.detailStorage.movementFilters?.currentMethods?.length) {
      return false;
    } else if (this.detailStorage.movementFilters.currentCashiers?.length) {
      return false;
    } else if (this.detailStorage.movementFilters.currentBranches?.length) {
      return false;
    } else {
      return true;
    }
  }

  emitFilterButtonPressed() {
    if (this.filterForm.value.dateFrom && !this.filterForm.value.dateTo) {
      return;
    }

    if (this.filterForm.invalid) {
      return;
    } else {
      this.filterEmitter.emit(this.filterForm.value);
      this.showOptions = false;
    }
  }

  emitCleanFiltersValues() {
    // Limpiar todos los campos excepto las fechas
    this.filterForm.patchValue({
      paymentMethods: null,
      paymentStates: null,
      branches: null,
      cashiers: null
    });

    // Restablecer las fechas al rango por defecto (últimos 31 días)
    const today = new Date();
    const thirtyOneDaysAgo = new Date();
    thirtyOneDaysAgo.setDate(today.getDate() - 31);

    this.filterForm.patchValue({
      dateFrom: thirtyOneDaysAgo,
      dateTo: today,
      hourFrom: '00',
      minutesFrom: '00',
      hourTo: '23',
      minutesTo: '59'
    });

    // Marcar los campos de fecha como untouched y pristine para quitar el estado de error
    this.filterForm.get('dateFrom')?.markAsUntouched();
    this.filterForm.get('dateFrom')?.markAsPristine();
    this.filterForm.get('dateTo')?.markAsUntouched();
    this.filterForm.get('dateTo')?.markAsPristine();

    // Actualizar el estado de los banners
    this.setMaxDate(thirtyOneDaysAgo);
    this.isDefaultDateRange = true;
    this.showDefaultRangeBanner = true;
    this.showExceeds31DaysBanner = false;

    // Emitir el evento con las fechas por defecto
    this.cleanFilterEmitter.emit(this.filterForm.value);
    this.showOptions = false;
  }

  emitRefreshList() {
    this.refreshListEmitter.emit(true);
  }

  emitDownloadExcel() {
    const dateFrom = this.filterForm.get('dateFrom').value;
    const dateTo = this.filterForm.get('dateTo').value;
    if (this.filterForm.valid) {
      const sucursales =
        this.filterForm.get('branches').value !== null
          ? this.filterForm.get('branches').value
          : this.branchesValuesForSelect;
      const sucursalesSelect = this.filterForm.get('branches').value !== null ? true : false;
      this.downloadExcelEmitter.emit({ dateFrom, dateTo, sucursales, sucursalesSelect });
      this.disableExport = true;
    }
  }

  // Seleccionar/deseleccionar todos para paymentMethods
  toggleAllPaymentMethods() {
    const allSelected = this.areAllPaymentMethodsSelected();
    this.filterForm.get('paymentMethods').setValue(allSelected ? [] : this.methodsValuesForSelect.map((m) => m.value));
  }

  areAllPaymentMethodsSelected(): boolean {
    const selected = this.filterForm.get('paymentMethods').value || [];
    const allValues = this.methodsValuesForSelect.map((m) => m.value);
    return selected.length === allValues.length && allValues.every((v) => selected.includes(v));
  }

  // Seleccionar/deseleccionar todos para paymentStates
  toggleAllPaymentStates() {
    const allSelected = this.areAllPaymentStatesSelected();
    this.filterForm.get('paymentStates').setValue(allSelected ? [] : this.statesValuesForSelect.map((s) => s.value));
  }

  areAllPaymentStatesSelected(): boolean {
    const selected = this.filterForm.get('paymentStates').value || [];
    const allValues = this.statesValuesForSelect.map((s) => s.value);
    return selected.length === allValues.length && allValues.every((v) => selected.includes(v));
  }

  // Seleccionar/deseleccionar todos para branches
  toggleAllBranches() {
    const allSelected = this.areAllBranchesSelected();
    this.filterForm.get('branches').setValue(allSelected ? [] : this.branchesValuesForSelect.map((b) => b.value));
  }

  areAllBranchesSelected(): boolean {
    const selected = this.filterForm.get('branches').value || [];
    const allValues = this.branchesValuesForSelect.map((b) => b.value);
    return selected.length === allValues.length && allValues.every((v) => selected.includes(v));
  }

  // Seleccionar/deseleccionar todos para cashiers
  toggleAllCashiers() {
    const allSelected = this.areAllCashiersSelected();
    this.filterForm.get('cashiers').setValue(allSelected ? [] : this.cashiersValuesForSelect.map((c) => c.value));
  }

  areAllCashiersSelected(): boolean {
    const selected = this.filterForm.get('cashiers').value || [];
    const allValues = this.cashiersValuesForSelect.map((c) => c.value);
    return selected.length === allValues.length && allValues.every((v) => selected.includes(v));
  }

  /**
   * Establece el rango de fechas por defecto: desde hoy hasta 31 días atrás
   */
  setDefaultDateRange(): void {
    const today = new Date();
    const thirtyOneDaysAgo = new Date();
    thirtyOneDaysAgo.setDate(today.getDate() - 31);

    // Establecer maxDate antes de patchValue
    this.setMaxDate(thirtyOneDaysAgo);

    // Establecer las fechas en el formulario con emitEvent false para evitar loops
    this.filterForm.patchValue(
      {
        dateFrom: thirtyOneDaysAgo,
        dateTo: today
      },
      { emitEvent: false }
    );

    this.isDefaultDateRange = true;
    this.showDefaultRangeBanner = true;
    this.showExceeds31DaysBanner = false;

    // Emitir evento inmediatamente con las fechas por defecto
    setTimeout(() => {
      this.dateRangeChangedEmitter.emit({
        dateFrom: thirtyOneDaysAgo,
        dateTo: today,
        isDefault: true
      });
      // Forzar la actualización de la vista
      this.filterForm.updateValueAndValidity();
    }, 0);
  }

  /**
   * Verifica si el rango de fechas ha cambiado del rango por defecto
   * y actualiza los banners correspondientes
   */
  checkDateRangeChanges(): void {
    const dateFrom = this.filterForm.get('dateFrom')?.value;
    const dateTo = this.filterForm.get('dateTo')?.value;

    if (!dateFrom || !dateTo) {
      this.showDefaultRangeBanner = false;
      this.showExceeds31DaysBanner = false;
      this.dateRangeChangedEmitter.emit({ dateFrom: null, dateTo: null, isDefault: false });
      return;
    }

    // Verificar si es el rango por defecto
    const today = new Date();
    const thirtyOneDaysAgo = new Date();
    thirtyOneDaysAgo.setDate(today.getDate() - 31);

    const isDefaultFrom = this.isSameDay(dateFrom, thirtyOneDaysAgo);
    const isDefaultTo = this.isSameDay(dateTo, today);

    // El banner amarillo solo se muestra si es el rango por defecto Y no se ha cambiado manualmente
    if (isDefaultFrom && isDefaultTo && this.isDefaultDateRange) {
      this.showDefaultRangeBanner = true;
    } else {
      // Al cambiar cualquier fecha, ocultar el banner amarillo
      this.showDefaultRangeBanner = false;
      this.isDefaultDateRange = false;
    }

    // Verificar si excede 31 días
    const hasExceeds31DaysError = this.filterForm.errors?.dateRangeExceeds31Days;
    this.showExceeds31DaysBanner = !!hasExceeds31DaysError;

    // Emitir evento al padre con el rango de fechas
    this.dateRangeChangedEmitter.emit({
      dateFrom,
      dateTo,
      isDefault: isDefaultFrom && isDefaultTo
    });
  }

  /**
   * Compara si dos fechas son el mismo día (ignora horas, minutos, segundos)
   */
  private isSameDay(date1: Date, date2: Date): boolean {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  }

  /**
   * Verifica si el botón de filtro debe estar deshabilitado
   */
  isFilterButtonDisabled(): boolean {
    return this.filterForm.invalid || this.filterForm.errors?.dateRangeExceeds31Days;
  }

  /**
   * Obtiene el rango de fechas formateado para mostrar
   */
  getFormattedDateRange(): string {
    const dateFrom = this.filterForm.get('dateFrom')?.value;
    const dateTo = this.filterForm.get('dateTo')?.value;

    if (!dateFrom || !dateTo) {
      return '';
    }

    const formatDate = (date: Date): string => {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };

    return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
  }
}
